import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Button, Badge, Modal, Input } from '@components';
import { BusinessHoursPicker } from '@components/BusinessHoursPicker/BusinessHoursPicker';
import { ConnectedCalendar } from '../types';
import { syncService } from '../services/sync';
import { useToast } from '../../../contexts/useToast';
import { ProcessedErrors } from '@utils/errorHandler';
import { ApiClient } from '@utils/apiClient';
import { handleApiResult } from '@utils/apiErrorHandler';
import { validateDurationString, normalizeDurationString } from '@utils/timeSlotDuration';

interface ConnectedCalendarsProps {
  calendars: ConnectedCalendar[];
  onEdit: (id: string) => void;
  onDisconnect: (id: string) => void;
}

export const ConnectedCalendars: React.FC<ConnectedCalendarsProps> = ({
  calendars,
  onEdit: _onEdit,
  onDisconnect,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [items, setItems] = React.useState<ConnectedCalendar[]>(calendars);
  React.useEffect(() => {
    setItems(calendars);
  }, [calendars]);
  const [disconnectModal, setDisconnectModal] = React.useState<{
    isOpen: boolean;
    calendar: ConnectedCalendar | null;
  }>({ isOpen: false, calendar: null });
  const [editModal, setEditModal] = React.useState<{
    isOpen: boolean;
    calendar: ConnectedCalendar | null;
    name: string;
    timeslots: string[]; // Array of duration strings like "15min", "30min", "1h"
    timeslot_input: string; // Current input value
    timeslot_input_error: string | null; // Error message for timeslot input
    business_hours: string;
  }>({
    isOpen: false,
    calendar: null,
    name: '',
    timeslots: [],
    timeslot_input: '',
    timeslot_input_error: null,
    business_hours: '',
  });
  const [backendErrors, setBackendErrors] = React.useState<ProcessedErrors | null>(null);
  const [syncingId, setSyncingId] = React.useState<string | null>(null);

  const getFieldError = (field: string): string | undefined => {
    return backendErrors?.fieldErrors[field];
  };

  React.useEffect(() => {
    if (!editModal.isOpen) {
      setBackendErrors(null);
    }
  }, [editModal.isOpen]);
  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case 'google':
        return 'calendar_today';
      case 'outlook':
        return 'event';
      case 'apple':
        return 'event_note';
      case 'caldav':
        return 'calendar_view_month';
      case 'cal':
        return 'schedule';
      case 'ical':
        return 'link';
      default:
        return 'calendar_today';
    }
  };

  const getStatusBadge = (status: string, isConnected: boolean) => {
    if (isConnected) {
      return (
        <Badge variant="success" size="sm">
          {t('calendars.connected')}
        </Badge>
      );
    }

    switch (status) {
      case 'active':
        return (
          <Badge variant="info" size="sm">
            {t('calendars.active')}
          </Badge>
        );
      case 'inactive':
        return (
          <Badge variant="secondary" size="sm">
            {t('calendars.inactive')}
          </Badge>
        );
      case 'error':
        return (
          <Badge variant="error" size="sm">
            {t('calendars.error')}
          </Badge>
        );
      case 'expired':
        return (
          <Badge variant="warning" size="sm">
            {t('calendars.expired')}
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" size="sm">
            {status}
          </Badge>
        );
    }
  };

  if (items.length === 0) {
    return (
      <Card>
        <CardContent>
          <div className="text-center py-12">
            <span className="material-icons text-6xl text-gray-400 mb-4">calendar_today</span>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {t('calendars.noCalendarsConnected')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {t('calendars.connectFirstCalendarDescription')}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('calendars.calendarIntegration')}
            </h2>
          </div>

          <div className="space-y-4">
            {items.map(calendar => (
              <div
                key={calendar.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                      <span className="material-icons text-blue-600 dark:text-blue-400">
                        {getProviderIcon(calendar.provider)}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {calendar.name}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {calendar.organization}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {calendar.providerCalendarId}
                      </p>
                      {calendar.status === 'error' && calendar.errorMessage && (
                        <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                          {calendar.errorMessage}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(calendar.status, calendar.isConnected)}
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          try {
                            setSyncingId(calendar.id);
                            const start = Date.now();
                            await syncService.sync(calendar.id);
                            const elapsed = Date.now() - start;
                            const minDuration = 800; // ms
                            if (elapsed < minDuration) {
                              await new Promise(resolve =>
                                setTimeout(resolve, minDuration - elapsed)
                              );
                            }
                            showToast({
                              type: 'success',
                              title: t('calendars.syncSuccess') || 'Sync Successful',
                              message:
                                t('calendars.syncSuccessMessage') ||
                                'Calendar events synced successfully',
                            });
                          } catch (error) {
                            const errorMessage =
                              error instanceof Error
                                ? error.message
                                : t('calendars.syncError') || 'Failed to sync calendar events';
                            showToast({
                              type: 'error',
                              title: t('calendars.syncError') || 'Sync Failed',
                              message: errorMessage,
                              duration: 5000,
                            });
                          } finally {
                            setSyncingId(null);
                          }
                        }}
                        className="p-1 h-8 w-8"
                        title={t('common.refresh')}
                        disabled={syncingId === calendar.id}
                      >
                        <span
                          className="material-icons text-lg animate-spin"
                          style={{
                            animationPlayState: syncingId === calendar.id ? 'running' : 'paused',
                          }}
                        >
                          sync
                        </span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          let businessHours = calendar.business_hours || '';
                          if (
                            businessHours === '{}' ||
                            businessHours === 'null' ||
                            businessHours === 'undefined'
                          ) {
                            businessHours = '';
                          }
                          // Parse timeslots from backend (could be string, array, number, etc.)
                          let timeslotDurations: string[] = [];
                          if (calendar.timeslots) {
                            if (Array.isArray(calendar.timeslots)) {
                              timeslotDurations = calendar.timeslots.map(d =>
                                typeof d === 'string' ? d : normalizeDurationString(d)
                              );
                            } else if (typeof calendar.timeslots === 'string') {
                              // If it's a JSON string, try to parse it
                              try {
                                const parsed = JSON.parse(calendar.timeslots);
                                if (Array.isArray(parsed)) {
                                  timeslotDurations = parsed.map(d =>
                                    typeof d === 'string' ? d : normalizeDurationString(d)
                                  );
                                } else {
                                  timeslotDurations = [normalizeDurationString(parsed)];
                                }
                              } catch {
                                // If not JSON, treat as single string
                                timeslotDurations = [normalizeDurationString(calendar.timeslots)];
                              }
                            } else {
                              // It's a number
                              timeslotDurations = [normalizeDurationString(calendar.timeslots)];
                            }
                          }
                          setEditModal({
                            isOpen: true,
                            calendar,
                            name: calendar.name || '',
                            timeslots: timeslotDurations,
                            timeslot_input: '',
                            timeslot_input_error: null,
                            business_hours: businessHours,
                          });
                        }}
                        className="p-1 h-8 w-8"
                        title={t('calendars.editCalendar')}
                      >
                        <span className="material-icons text-lg">edit</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDisconnectModal({ isOpen: true, calendar })}
                        className="p-1 h-8 w-8"
                        title={t('calendars.disconnectCalendar')}
                      >
                        <span className="material-icons text-red-600 text-lg">link_off</span>
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                  <span>
                    {t('calendars.provider')}: {calendar.providerDisplay}
                  </span>
                  <span>•</span>
                  <span>
                    {calendar.appointmentCount} {t('calendars.appointments')}
                  </span>
                  <span>•</span>
                  <span>
                    {t('calendars.modified')}: {new Date(calendar.modified).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      {/* Disconnect Confirmation Modal */}
      <Modal
        isOpen={disconnectModal.isOpen}
        onClose={() => setDisconnectModal({ isOpen: false, calendar: null })}
        title=""
        size="sm"
        showCloseButton={false}
      >
        <div className="text-center space-y-6">
          <div className="mx-auto w-16 h-16 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center">
            <span className="material-icons text-3xl text-red-600 dark:text-red-400">link_off</span>
          </div>
          <div className="space-y-3">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('calendars.disconnectCalendar')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              {t('common.areYouSure')}{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                &ldquo;{disconnectModal.calendar?.name}&rdquo;
              </span>
              ?
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              {t('common.thisActionCannotBeUndone')}
            </p>
          </div>
          <div className="flex gap-3 pt-2">
            <Button
              variant="secondary"
              onClick={() => setDisconnectModal({ isOpen: false, calendar: null })}
              className="flex-1 px-6 py-3 font-medium"
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (disconnectModal.calendar) {
                  // Optimistic UI: remove locally
                  setItems(prev => prev.filter(c => c.id !== disconnectModal.calendar!.id));
                  onDisconnect(disconnectModal.calendar.id);
                }
                setDisconnectModal({ isOpen: false, calendar: null });
              }}
              className="flex-1 px-6 py-3 font-medium"
            >
              <span className="material-icons text-sm mr-2">link_off</span>
              {t('calendars.disconnect')}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={editModal.isOpen}
        onClose={() =>
          setEditModal({
            isOpen: false,
            calendar: null,
            name: '',
            timeslots: [],
            timeslot_input: '',
            timeslot_input_error: null,
            business_hours: '',
          })
        }
        title={t('calendars.editCalendar')}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label
              htmlFor="calendar-name"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('calendars.calendarName')}
            </label>
            <Input
              id="calendar-name"
              value={editModal.name}
              onChange={e => setEditModal(prev => ({ ...prev, name: e.target.value }))}
              placeholder={t('calendars.calendarName')}
            />
          </div>

          <div>
            <label
              htmlFor="timeslot-duration"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('calendars.timeslotDuration') || 'Time Slot Durations'}
            </label>
            <div className="space-y-2">
              <Input
                id="timeslot-duration"
                value={editModal.timeslot_input}
                onChange={e => {
                  setEditModal(prev => ({
                    ...prev,
                    timeslot_input: e.target.value,
                    timeslot_input_error: null, // Clear error on input change
                  }));
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const input = editModal.timeslot_input.trim();
                    if (!input) {
                      setEditModal(prev => ({
                        ...prev,
                        timeslot_input_error: null,
                      }));
                      return;
                    }

                    const validation = validateDurationString(input);
                    if (!validation.isValid) {
                      setEditModal(prev => ({
                        ...prev,
                        timeslot_input_error: validation.error || 'Invalid duration format',
                      }));
                      return;
                    }

                    const normalized = normalizeDurationString(input);
                    if (!editModal.timeslots.includes(normalized)) {
                      setEditModal(prev => ({
                        ...prev,
                        timeslots: [...prev.timeslots, normalized],
                        timeslot_input: '',
                        timeslot_input_error: null,
                      }));
                    } else {
                      setEditModal(prev => ({
                        ...prev,
                        timeslot_input_error: 'This duration is already added',
                      }));
                    }
                  }
                }}
                placeholder={t('calendars.timeslotDurationPlaceholder') || 'e.g., 15min, 30min, 1h'}
                className="w-full"
                state={editModal.timeslot_input_error ? 'error' : 'default'}
                errorText={editModal.timeslot_input_error || undefined}
              />
              {editModal.timeslots.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {editModal.timeslots.map((duration, index) => (
                    <div
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-md text-sm"
                    >
                      <span>{duration}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setEditModal(prev => ({
                            ...prev,
                            timeslots: prev.timeslots.filter((_, i) => i !== index),
                          }));
                        }}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                        aria-label="Remove"
                      >
                        <span className="material-icons text-sm">close</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('calendars.timeslotDurationHint') ||
                  'Enter durations like "15min", "30min", "1h" and press Enter'}
              </p>
            </div>
          </div>

          <div>
            <label
              htmlFor="business-hours"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('calendars.businessHours') || 'Business Hours'}
            </label>
            <BusinessHoursPicker
              value={editModal.business_hours}
              onChange={value => {
                setEditModal(prev => ({ ...prev, business_hours: value }));
                if (backendErrors?.fieldErrors.business_hours) {
                  setBackendErrors(prev => {
                    if (!prev) return null;
                    const newFieldErrors = { ...prev.fieldErrors };
                    delete newFieldErrors.business_hours;
                    return { ...prev, fieldErrors: newFieldErrors };
                  });
                }
              }}
              error={getFieldError('business_hours')}
            />
          </div>

          <div className="flex gap-3 justify-end pt-4">
            <Button
              variant="secondary"
              onClick={() =>
                setEditModal({
                  isOpen: false,
                  calendar: null,
                  name: '',
                  timeslots: [],
                  timeslot_input: '',
                  timeslot_input_error: null,
                  business_hours: '',
                })
              }
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={async () => {
                if (!editModal.calendar) return;
                setBackendErrors(null);
                try {
                  const updateData: {
                    name: string;
                    timeslots?: string | string[];
                    business_hours?: string;
                  } = {
                    name: editModal.name,
                  };

                  // Send timeslots as array of strings (always as array, even if only one)
                  if (editModal.timeslots.length > 0) {
                    updateData.timeslots = editModal.timeslots;
                  }

                  if (editModal.business_hours.trim()) {
                    updateData.business_hours = editModal.business_hours.trim();
                  }

                  const response = await ApiClient.patch(
                    `/calendars/calendars/${editModal.calendar.id}/`,
                    updateData
                  );
                  const result = await handleApiResult(response, 'Failed to update calendar');

                  if (!result.success) {
                    if (
                      result.errors?.fieldErrors &&
                      Object.keys(result.errors.fieldErrors).length > 0
                    ) {
                      setBackendErrors(result.errors);
                      return;
                    }
                    const errorMessage =
                      result.errors?.generalError ||
                      t('calendars.updateError') ||
                      'Failed to update calendar';
                    showToast({
                      type: 'error',
                      title: t('common.error'),
                      message: errorMessage,
                    });
                    return;
                  }

                  showToast({
                    type: 'success',
                    title: t('common.success'),
                    message: t('calendars.calendarUpdated') || 'Calendar updated successfully',
                  });
                  setItems(prev =>
                    prev.map(c =>
                      c.id === editModal.calendar!.id
                        ? {
                            ...c,
                            name: editModal.name,
                            ...(editModal.timeslots.length > 0 && {
                              timeslots: editModal.timeslots,
                            }),
                            ...(editModal.business_hours.trim() && {
                              business_hours: editModal.business_hours.trim(),
                            }),
                          }
                        : c
                    )
                  );
                  setEditModal({
                    isOpen: false,
                    calendar: null,
                    name: '',
                    timeslots: [],
                    timeslot_input: '',
                    timeslot_input_error: null,
                    business_hours: '',
                  });
                  setBackendErrors(null);
                } catch (error) {
                  const errorMessage =
                    error instanceof Error
                      ? error.message
                      : t('calendars.updateError') || 'Failed to update calendar';
                  showToast({
                    type: 'error',
                    title: t('common.error'),
                    message: errorMessage,
                  });
                }
              }}
              disabled={!editModal.name.trim()}
            >
              {t('common.save')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
